import { JsonObject, JsonProperty } from "typescript-json-serializer";
import {LayerConfiguration} from "./layer-configuration";
import {PortalItemConfiguration} from "./portal-item-configuration";
import {SpatialReferenceConfiguration} from "./spatial-reference-configuration";
import {ColorConfiguration} from "./color-configuration";

@JsonObject()
export class  BasemapStyleConfiguration {
    /**
     * An authorization string used to access a resource or service.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-support-BasemapStyle.html#apiKey)
     */
    @JsonProperty()
    declare apiKey?: string;

    /**
     * The id of the basemap style.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-support-BasemapStyle.html#id)
     */
    @JsonProperty()
    declare id?: string;
    /**
     * The language of the place labels in the basemap style.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-support-BasemapStyle.html#language)
     */
    @JsonProperty()
    declare language?: string;
    /**
     * Indicates whether to display [places](https://developers.arcgis.com/javascript/latest/api-reference/esri-rest-places.html) with the basemap style.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-support-BasemapStyle.html#places)
     */
    @JsonProperty()
    declare places?: "all" | "attributed" | "none";
    /**
     * The [URL](https://developers.arcgis.com/rest/basemap-styles/#service-url) to the basemap styles service.
     *
     * @default "https://basemapstyles-api.arcgis.com/arcgis/rest/services/styles/v2/webmaps"
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-support-BasemapStyle.html#serviceUrl)
     */
    @JsonProperty()
    declare serviceUrl?: string;
    /**
     * Displays country boundaries and labels based on a specific view of a country.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-support-BasemapStyle.html#worldview)
     */
    @JsonProperty()
    declare worldview?: string;
}

@JsonObject()
export class BasemapConfiguration {
    /**
     * A collection of tile layers that make up the basemap's features.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-Basemap.html#baseLayers)
     */
    @JsonProperty()
    declare baseLayers: LayerConfiguration[];

    /**
     * An identifier used to refer to the basemap when referencing it elsewhere.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-Basemap.html#id)
     */
    @JsonProperty()
    declare id: string;

    /**
     * The portal item.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-Basemap.html#portalItem)
     */
    @JsonProperty()
    declare portalItem: PortalItemConfiguration;

    /**
     * A collection of reference layers which are displayed over the [base layers](https://developers.arcgis.com/javascript/latest/api-reference/esri-Basemap.html#baseLayers) and all other layers in the map.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-Basemap.html#referenceLayers)
     */
    @JsonProperty()
    declare referenceLayers: LayerConfiguration[];

    /**
     * The spatial reference of the Basemap.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-Basemap.html#spatialReference)
     */
    @JsonProperty()
    declare spatialReference: SpatialReferenceConfiguration;

    /**
     * The style of the basemap from the [basemap styles service (v2)](https://developers.arcgis.com/rest/basemap-styles/).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-Basemap.html#style)
     */
    @JsonProperty()
    declare style?: BasemapStyleConfiguration;

    /**
     * The URL pointing to an image that represents the basemap.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-Basemap.html#thumbnailUrl)
     */
    @JsonProperty()
    declare thumbnailUrl: string;

    /**
     * The title of the basemap.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-Basemap.html#title)
     */
    @JsonProperty()
    declare title: string;

    constructor() {}
}

@JsonObject()
export class GroundConfiguration {
    /**
     * A collection of [ElevationLayers](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-ElevationLayer.html) that define the elevation or terrain that makes up the ground surface.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-Ground.html#layers)
     */
    // layers?: CollectionProperties<
    //     (ElevationLayerProperties & { type: "elevation" }) | (BaseElevationLayerProperties & { type: "base-elevation" })
    // >;
    /**
     * Specifies the user navigation constraints relative to the ground surface.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-Ground.html#navigationConstraint)
     */
    // navigationConstraint?: GroundNavigationConstraintProperties;
    /**
     * Opacity of the ground, including surface default color and the basemap (without reference layers).
     *
     * @default 1
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-Ground.html#opacity)
     */
    @JsonProperty()
    declare opacity?: number;
    /**
     * The color of the ground surface, displayed underneath the basemap.
     *
     * @default null
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-Ground.html#surfaceColor)
     */
    @JsonProperty()
    declare surfaceColor?: ColorConfiguration | number[] | string;

    constructor() {}
}

@JsonObject()
export class MapConfiguration {
    /**
     * Specifies a basemap for the map. The basemap is a set of tile layers that give geographic context to the [MapView](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html) or [SceneView](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-SceneView.html) and the other [operational layers](https://developers.arcgis.com/javascript/latest/api-reference/esri-Map.html#layers) in the map.  This value can be an instance of [Basemap](https://developers.arcgis.com/javascript/latest/api-reference/esri-Basemap.html) or one of the strings listed in the table below.
     *
     * Value | Source | Thumbnail
     * ------|------- |----------
     * `topo`                | https://services.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer   | ![basemap-topo](https://developers.arcgis.com/javascript/assets/img/apiref/basemap/topo.jpg)
     * `streets`             | https://services.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer | ![basemap-streets](https://developers.arcgis.com/javascript/assets/img/apiref/basemap/streets.jpg)
     * `satellite`           | https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer    | ![basemap-satellite](https://developers.arcgis.com/javascript/assets/img/apiref/basemap/satellite.jpg)
     * `hybrid`              | https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer   and  https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer            | ![basemap-hybrid](https://developers.arcgis.com/javascript/assets/img/apiref/basemap/hybrid.jpg)
     * `dark-gray`           | https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer        and  https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer   | ![basemap-dark-gray](https://developers.arcgis.com/javascript/assets/img/apiref/basemap/dark-gray.jpg)
     * `gray`                | https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer       and  https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer | ![basemap-gray](https://developers.arcgis.com/javascript/assets/img/apiref/basemap/gray.jpg)
     * `national-geographic` | https://services.arcgisonline.com/ArcGIS/rest/services/NatGeo_World_Map/MapServer | ![basemap-national-geographic](https://developers.arcgis.com/javascript/assets/img/apiref/basemap/national-geographic.jpg)
     * `oceans`              | https://services.arcgisonline.com/arcgis/rest/services/Ocean/World_Ocean_Reference/MapServer             and  https://services.arcgisonline.com/arcgis/rest/services/Ocean/World_Ocean_Base/MapServer | ![basemap-oceans](https://developers.arcgis.com/javascript/assets/img/apiref/basemap/oceans.jpg)
     * `osm`                 | [OpenStreetMapLayer](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-OpenStreetMapLayer.html) | ![basemap-osm](https://developers.arcgis.com/javascript/assets/img/apiref/basemap/osm.jpg)
     * `terrain`             | https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Reference_Overlay/MapServer       and  https://services.arcgisonline.com/ArcGIS/rest/services/World_Terrain_Base/MapServer | ![basemap-terrain](https://developers.arcgis.com/javascript/assets/img/apiref/basemap/terrain.jpg)
     * `dark-gray-vector`          | [Dark Gray Canvas [v2]](https://www.arcgis.com/home/item.html?id=c11ce4f7801740b2905eb03ddc963ac8)         | ![dark-gray-vector](https://developers.arcgis.com/javascript/assets/img/apiref/basemap/dark-gray.jpg)
     * `gray-vector`               | [Light Gray Canvas [v2]](https://www.arcgis.com/home/item.html?id=8a2cba3b0ebf4140b7c0dc5ee149549a)        | ![gray-vector](https://developers.arcgis.com/javascript/assets/img/apiref/basemap/gray.jpg)
     * `streets-vector`            | [World Street Map [v2]](https://www.arcgis.com/home/item.html?id=de26a3cf4cc9451298ea173c4b324736)         | ![streets-vector](https://developers.arcgis.com/javascript/assets/img/apiref/basemap/streets.jpg)
     * `streets-night-vector`      | [World Street Map (Night) [v2]](https://www.arcgis.com/home/item.html?id=86f556a2d1fd468181855a35e344567f) | ![streets-night-vector](https://developers.arcgis.com/javascript/assets/img/apiref/basemap/streets-night.jpg)
     * `streets-navigation-vector` | [World Navigation Map [v2]](https://www.arcgis.com/home/item.html?id=63c47b7177f946b49902c24129b87252)     | ![streets-navigation-vector](https://developers.arcgis.com/javascript/assets/img/apiref/basemap/streets-navigation.jpg)
     * `topo-vector`               | https://services.arcgisonline.com/arcgis/rest/services/Elevation/World_Hillshade/MapServer   and  [World Topographic Map [v2]](https://www.arcgis.com/home/item.html?id=7dc6cea0b1764a1f9af2e679f642f0f5) | ![topo-vector](https://developers.arcgis.com/javascript/assets/img/apiref/basemap/topo.jpg)
     * `streets-relief-vector`     | https://services.arcgisonline.com/arcgis/rest/services/Elevation/World_Hillshade/MapServer   and  [World Street Map (with Relief) [v2]](https://www.arcgis.com/home/item.html?id=b266e6d17fc345b498345613930fbd76) | ![streets-relief-vector](https://developers.arcgis.com/javascript/assets/img/apiref/basemap/streets-relief.jpg)
     *
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-Map.html#basemap)
     */
    @JsonProperty()
    declare basemap: BasemapConfiguration;

    /**
     * Specifies the surface properties for the map. This property is only relevant when adding the map to a 3D [SceneView](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-SceneView.html). It renders the terrain or topographical variations in the real world on the map's surface with a collection of [ElevationLayer](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-ElevationLayer.html).  This value can be an instance of [Ground](https://developers.arcgis.com/javascript/latest/api-reference/esri-Ground.html), or one of the following strings:
     *   * `world-elevation` for a default instance of ground using the [Terrain3D Service](https://www.arcgis.com/home/item.html?id=7029fb60158543ad845c7e1527af11e4).
     *   * `world-topobathymetry` for an instance of ground that combines surface elevation and bathymetry using the [TopoBathy3D Service](https://www.arcgis.com/home/item.html?id=0c69ba5a5d254118841d43f03aa3e97d).
     *
     *
     * The ground may not be set to `null` or `undefined`, it is guaranteed to always contain an instance of type [Ground](https://developers.arcgis.com/javascript/latest/api-reference/esri-Ground.html). The elevation can be removed from the ground by setting the ground property to a new empty [Ground](https://developers.arcgis.com/javascript/latest/api-reference/esri-Ground.html) instance or by removing all the ground layers.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-Map.html#ground)
     */
    @JsonProperty()
    declare ground: GroundConfiguration | string;

    constructor() {}
}
